import React, { useState, useEffect, useContext, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTracking } from '../../utils/useTracking'
import { Prompt } from 'react-router'
import { useHistory } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import { Tour } from 'antd'
import { APIContext } from '../../utils/api'
import { GlobalContext } from '../../utils/globalState'
import { getMediaTypeById } from '../../utils/mediaTypes'
import iconAnalysis from '../../assets/images/icon-analysis.svg'
import keysLeft from '../../assets/images/tag-keys-left-arrow.svg'
import keysSpace from '../../assets/images/tag-keys-space-bar.svg'
import keysRight from '../../assets/images/tag-keys-right-arrow.svg'
import EasyTagging from './EasyTagging'
import styles from './styles.module.css'

// component function
export default function AnalysisTagging(props) {

	const { title, guid } = props.data
	const context = useContext(APIContext)
	const history = useHistory()
	const { track } = useTracking('tagging')
	const [globalState, setGlobalState] = useContext(GlobalContext)
	const [video, setVideo] = useState()
	const [forceUpdate, setForceUpdate] = useState(0)
	const [chunksLoaded, setChunksLoaded] = useState(false)
	const [submitted, setSubmitted] = useState(false)
	const [saving, setSaving] = useState(false)
	const [finishing, setFinishing] = useState(false)
	const [submitProgress, setSubmitProgress] = useState(0)
	const [hasFinished, setHasFinished] = useState(false)
	const chunkArrayRef = useRef([])
	const origSegmentRef = useRef([])
	const objectsToUpdateRef = useRef([])
	const segmentsToAddRef = useRef([])
	const objectsToAddRef = useRef([])
	const objectIdsToDeleteRef = useRef([])
	const segmentIdsToDeleteRef = useRef([])
	const videoRef = useRef()
	const savedRef = useRef(false)
	const maxWidth = 1080

	let numChunksLoaded = 0

	// Tour
	const ref1 = useRef(null)
	const ref2 = useRef(null)
	const ref3 = useRef(null)
	const ref4 = useRef(null)
	const ref5 = useRef(null)
	const ref6 = useRef(null)
	const ref7 = useRef(null)
	const ref8 = useRef(null)
	const ref9 = useRef(null)
	const ref10 = useRef(null)
	const [open, setOpen] = useState(!globalState.userData?.settings?.taggingTourDone)
	const steps = [
		{
			title: 'Tag your brand assets',
			description: 'Flowsam has detected some objects in your ' + (video?.analysisType !== 3 ? 'video' : 'image') + '. Step through them one by one and select those you identify as distinctive brand assets or branded communication for inclusion in the analysis.'
		},
		{
			title: 'Object',
			description: 'Tap the space key or Click an object to tag it as an asset and include it in the analysis. Flowsam automatically marks tagged assets as Brand (green) or Communication (orange).',
			target: () => ref1.current
		},
		{
			title: 'Asset label',
			description: 'You can change the label of an asset by clicking its text or pressing the return key. You can also edit asset labels later in the report.',
			target: () => ref2.current
		},
		{
			title: 'Asset type',
			description: 'Once an object has been tagged as asset, you can specify what type of brand- or communication asset it is - such as logo, product, headline, CTA, etc. This can also be added later in the report.',
			target: () => ref3.current
		},
		{
			title: 'Tagging progress',
			description: 'Shows how far you are in the tagging process.',
			target: () => ref4.current
		},
		{
			title: 'Previous object',
			description: 'Click the left button or use left arrow key to go to the previous object.',
			cover: (<img alt="Keyboard left" src={keysLeft} />),
			target: () => ref5.current
		},
		{
			title: 'Tag as asset/untag asset',
			description: 'Click the middle button or tap the space key to tag the object as an asset and include it in the analysis.',
			cover: (<img alt="Space key" src={keysSpace} />),
			target: () => ref6.current
		},
		{
			title: 'Don’t tag (or Next object)',
			description: 'Click the right button or use the right arrow key to go to the next object.',
			cover: (<img alt="Keyboard right" src={keysRight} />),
			target: () => ref7.current
		},
		{
			title: 'Save',
			description: 'Save your work during asset tagging if you’re not ready to finish the analysis. If the button is disabled, there are no changes to save.',
			target: () => ref8.current
		},
		{
			title: 'Finish & go to the report',
			description: 'Save and finish the analysis. This will take you to the analysis report. You can always come back later and edit your tagging. If the button is disabled, there are no changes to save or no assets have been tagged.',
			target: () => ref9.current
		},
		{
			title: 'Guide',
			description: 'You can always revisit this guide by clicking here.',
			target: () => ref10.current,
			nextButtonProps: {children:(<span>&nbsp;OK&nbsp;</span>)}
		}
	]

	// get video data on mount, guid change or history change
	useEffect(() => {
		setChunksLoaded(false)
		chunkArrayRef.current = []
		origSegmentRef.current = []
		context.io.socket.get('/api/v1/video/' + guid, (data, res) => {
			if (res.statusCode === 200) {
				if (data.guid) {
					// remove custom objects with frameCount exceeding reduced video length (due to Google not detecting objects in last 3 frames)
					data.objects = data.objects.filter(obj => obj.frameNo < data.frameCount-2)

					// object size threshold
					const thresholdW = 40 // HARD-CODED / TESTING // HJ
					const thresholdH = 40 // HARD-CODED / TESTING // HJ

					// calculate video css sizes
					const processedWidth = data.width * data.processing_scale
					const processedHeight = data.height * data.processing_scale * (1/data.streamInfo.pixelAspectRatio)
					const canvasHeightCss = maxWidth/640*360
					let canvasWidthCss = maxWidth
					if (processedHeight/processedWidth > 360/640) {
						canvasWidthCss = Math.ceil(processedWidth/processedHeight * canvasHeightCss)
					}

					// remove objects below threshold size
					data.objects = data.objects.filter(obj => obj.w * canvasWidthCss > thresholdW || obj.h * canvasHeightCss > thresholdH)

					// remove segments with no objects
					data.segments = data.segments.filter(seg => data.objects.find(o => o.segment === seg.id))

					// deep clone segment array for change reference
					origSegmentRef.current = data.segments.map(seg => ({...seg}))
					videoRef.current = data
					setVideo(videoRef.current)
				} else {
					history.push('/notfound')
				}
			} else {
				// TODO: error handling
			}
		})
	}, [context.io.socket, guid, history])

	// subscribe to events for this analysis
	useEffect(() => {
		context.io.socket.post('/api/v1/user/subscribe', { roomName: 'video-' + guid }, (data, res) => {
			if (res.statusCode !== 200) {
				// TODO: handle error
			}
		})
	}, [context.io.socket, guid])

	// conditional window unload listener
	useEffect(() => {
		hasChanges && !submitted ? window.addEventListener('beforeunload', leaveWarning) : window.removeEventListener('beforeunload', leaveWarning)
		return () => window.removeEventListener('beforeunload', leaveWarning) // cleanup on unmount
	})

	// warning before leaving if segment has changes
	function leaveWarning(e) {
		e.preventDefault()
		e.returnValue = 'If you leave now your asset tagging and label changes will be lost'
	}

	// cache image chunks
	if (video && !chunksLoaded && !chunkArrayRef.current.length) {
		const tmpImg = new Image()
		tmpImg.src = process.env.REACT_APP_GCS_BUCKET_URL + '/' + guid + '/maps-frames/' + guid + '-chunk' + ("0000000"+chunkArrayRef.current.length).slice(-7) + '.jpg'
		tmpImg.onload = checkLoaded
		chunkArrayRef.current.push(tmpImg)
	}

	// check that all chunks are loaded
	function checkLoaded(e) {
		e.target.onload = null
		if (!document.getElementById('chunk-load-pct')) {
			return
		}
		numChunksLoaded++
		if (numChunksLoaded === video.chunks) {
			setChunksLoaded(true)
		} else {
			document.getElementById('chunk-load-pct').innerHTML = (50*numChunksLoaded/video.chunks).toFixed(0) + '%'
		}
		if (chunkArrayRef.current.length < video.chunks) {
			const tmpImg = new Image()
			tmpImg.src = process.env.REACT_APP_GCS_BUCKET_URL + '/' + video.guid + '/maps-frames/' + video.guid + '-chunk' + ("0000000"+chunkArrayRef.current.length).slice(-7) + '.jpg'
			tmpImg.onload = checkLoaded
			chunkArrayRef.current.push(tmpImg)
		}
	}

	// submit selected cues to backend
	function submitCues(save) {
		track('button_click', {action: (save ? 'save' : 'save & finish')})
		save ? setSaving(true) : setFinishing(true)
		const selectedSegIds = video.segments.filter(seg => seg.cueType !== null).map(seg => seg.id)

		// FOR TEST ONLY: CLEAR CUETYPES
		//video.segments.filter(seg => seg.cueType !== null).map(seg => seg.cueType = null)
		//const updatedSegs = video.segments.filter(seg => seg.cueType === null)

		const updatedSegs = video.segments.filter(seg => seg.dirty)
		video.frontendState = { ...video.frontendState, status: save ? 'work-in-progress' : 'finished' }
		const updateRequest = {
			segIdsToDelete: segmentIdsToDeleteRef.current,
			objIdsToDelete: objectIdsToDeleteRef.current,
			segsToUpdate: updatedSegs,
			segsToAdd: segmentsToAddRef.current,
			objsToUpdate: objectsToUpdateRef.current,
			objsToAdd: objectsToAddRef.current,
			objectSegmentFilter: selectedSegIds,
			project: video.project?.id,
			frontendState: video.frontendState
		}
		// send cue selection data
		context.io.socket.post('/api/v1/video/' + guid + (save ? '?dont_calculate' : ''), updateRequest, (data, res) => {
			if (res.statusCode === 200) {
				if (save) {
					// reset after save
					objectsToAddRef.current = []
					objectsToUpdateRef.current = []
					objectIdsToDeleteRef.current = []
					segmentIdsToDeleteRef.current = []
					segmentsToAddRef.current = []
					video.segments.map(seg => seg.dirty = false)
					origSegmentRef.current = video.segments.map(seg => ({...seg})) // deep clone segment array for change reference
					savedRef.current = true
					setSaving(false)
				} else {
					// listen for succesful verification event
					setSubmitted(true)
					context.io.socket.off('video-recalc-' + guid)
					context.io.socket.on('video-recalc-' + guid, msg => { // TODO: remove listener after receiving data
						history.push('/report/' + guid)
					})
					context.io.socket.off('video-progress-' + guid)
					context.io.socket.on('video-progress-' + guid, msg => { // TODO: remove listener after receiving data
						if (msg.done.statisticsCalc < 100) {
							setSubmitProgress(msg.done.statisticsCalc)
						}
					})
				}
			} else {
				// TODO: error handling
			}
		})
	}

	// save Tour done in user settings if not already saved
	function saveTaggingTourDone() {
		if (!globalState.userData?.settings?.taggingTourDone) {
			const settings = { ...globalState.userData?.settings, taggingTourDone:true }
			context.io.socket.patch('/api/v1/user', { settings:settings }, (data, res) => { // save taggingTourDone flag in user settings
				if (res.statusCode === 200) {
					setGlobalState({ ...globalState, userData:{ ...data, settings:settings } })
				} else {
					// TODO: error handling
				}
			})
		}
		setOpen(false)
	}

	// new analysis button click handler
	function handleReportClick() {
		history.goBack()
	}

	// activate submit button if a segment has been edited and at least one cue is tagged
	const hasChanges = video && (video.segments.filter(seg => seg.dirty).length > 0 || segmentsToAddRef.current.length > 0 || segmentIdsToDeleteRef.current.length > 0 || objectsToAddRef.current.length > 0 || objectsToUpdateRef.current.length > 0 || objectIdsToDeleteRef.current.length > 0)
	const canSubmit = (hasChanges && video.segments.filter(seg => seg.cueType !== null).length > 0)

	// submit buttom or beatloader
	const inProgress = video?.frontendState?.status === 'work-in-progress'
	const submitButtons = (finishing ?
		<div>
			<div className={styles.sumbitLoader}><BeatLoader color={'var(--bg3-color)'} /></div>
			<div className={styles.submitLoaderPct}>{submitProgress}%</div>
		</div> :
		<div style={{width:'100%'}}>
			<button ref={ref8} className={'fs-button fs-button-blue ' + styles.button + ' ' + (!hasChanges ? '' : styles.hasChanges)} tabIndex="-1" onClick={e=>submitCues(true)}>{saving ? '\u00A0\u00A0SAVING...' : 'SAVE' + (savedRef.current && !hasChanges ? 'D' : '')}</button>
			<button ref={ref9} className={'fs-button fs-button-blue ' + styles.button + ' ' + (!canSubmit && !inProgress ? '' : styles.hasChanges) + ' ' + (hasFinished && canSubmit ? styles.hasFinished : '')} tabIndex="-1" onClick={e=>submitCues(false)}>SAVE & FINISH</button>
		</div>
	)

	// back to report-button if previous page vas a report
	const backToReportButton = (history?.location?.state?.from.indexOf('/report/' !== -1) && !finishing && !inProgress ?
		<div className={styles.reportButton} onClick={handleReportClick}>&lt;&nbsp;&nbsp;Back to report&nbsp;&nbsp;</div> :
		<div className={styles.reportButton} style={{cursor:'default'}} />
	)

	// video canvas section
	const canvas = (chunksLoaded &&
		<EasyTagging
			chunks={chunkArrayRef.current}
			video={video}
			origSegments={origSegmentRef.current}
			forceUpdate={e=>setForceUpdate(forceUpdate+Math.random())}
			setHasFinished={setHasFinished}
			objRef1={ref1}
			objRef2={ref2}
			objRef3={ref3}
			objRef4={ref4}
			objRef5={ref5}
			objRef6={ref6}
			objRef7={ref7}
	 	/>
	)
	
	// find media type icon by targetPlatform id
	const mediaType = getMediaTypeById(video?.targetPlatform)
	const mediaTypeIcon = mediaType !== undefined ? require('../../assets/images/media-type-icons/' + mediaType.icon) : iconAnalysis

	// analysis cues content
	return (
		<>
			<Helmet>
				<title>Asset tagging | Flowsam</title>
			</Helmet>
			{// no chunks or video yet - show loader
			!video || !video.guid || !chunksLoaded ?
				<div className={styles.loaderWrap}>
					<div className={styles.loader}>
						<BeatLoader color={'var(--bg3-color)'} />
					</div>
					<div id="chunk-load-pct" className={styles.loaderPct}>0%</div>
				</div>
			:
			// we got video and chunks - show content
			<div className={styles.container}>
				<h2 className={styles.heading}><img className={styles.icon} style={{transform: 'scale(1.3) translateY(' + (mediaType?.vpos * 1.4 - 3) + 'px)'}} src={mediaTypeIcon} alt="Analysis icon" /><div className={styles.title}>{!title && video ? video.title : title}</div></h2>
				<h3 className={styles.subHeading}>Tag the assets that are relevant to your brand or communication
					<div style={{display: 'inline-block'}}>
						<div ref={ref10} className={styles.infoButton} onClick={()=>setOpen(true)}>?</div>
					</div>
				</h3>
				<div className={styles.videoContainer}>
					{canvas}
				</div>
				{submitButtons}
				{backToReportButton}
				<Prompt when={hasChanges && !submitted} message="If you leave now your asset tagging and label changes will be lost" />
				<Tour open={open} steps={steps} zIndex={10001} onClose={saveTaggingTourDone}/>
			</div>}
		</>
	)
}
